import { useTranslation } from '@/utils/i18n/useTranslation';

import { useMatchLog } from '@/context/MatchContext';

import PlayerGraph from '@/components/PlayerGraph/PlayerGraph';
import { Fragment, useContext } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { UserContext } from '@/context/UserContext';
import type { Player } from '@/types/domain';

interface PlayerListProps {
  modifiers?: string[];
  playerList?: (Player[] | undefined)[];
  type?: string;
  clickablePlayers?: string | boolean;
  view?: string;
  selectedSpot?: number | null;
  setSelectedSpot?: (spot: number | null) => void;
  setSelectedModalPlayer?: (player: Player | null) => void;
  newInitialLineUp?: Player[];
  setNewInitialLineUp?: (lineUp: Player[]) => void;
  totalPlayed?: number;
  showPositions?: boolean;
  editMode?: boolean;
  players?: Player[];
  editLineUp?: Player[];
  setEditLineUp?: (lineUp: Player[]) => void;
}

interface PlayerListComponent {
  (props: PlayerListProps): ReactElement;
  defaultPropTypes?: Record<string, unknown>;
}

const PlayerList: PlayerListComponent = (props: PlayerListProps) => {
  const {
    modifiers,
    playerList,
    type,
    clickablePlayers,
    view,
    selectedSpot,
    setSelectedSpot,
    setSelectedModalPlayer,
    newInitialLineUp,
    setNewInitialLineUp,
    totalPlayed,
    showPositions,
    editMode,
    players,
    editLineUp,
    setEditLineUp,
  } = props;

  const modifierClasses = modifiers ? ' ' + modifiers.join(' ') : '';
  const {
    dispatch,
    substitutes,
    currentLineUp,
    initialLineUp,
    setShowSidebar,
    sidebarTabNav,
    setSidebarTabNav,
    playerFormationRef,
    setSelectedPlayer,
    selectedPlayer,
    match,
    visible,
  } = useMatchLog();

  const userContext = useContext(UserContext);
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  // Team matches duration (based on age group)
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const matchDuration = (
    match?.team?.[0] as { ageGroup?: { matchDuration?: number }[] }
  )?.ageGroup?.[0]?.matchDuration;
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  const handleChangeSubstitute = (player: Player) => {
    setShowSidebar(false);
    setSidebarTabNav('substitute');

    const newLineUp = currentLineUp.map((playerObj) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return playerObj.uuid === selectedPlayer!.uuid ? player : playerObj;
    });

    const newSubstitutes = substitutes.map((playerObj) => {
      /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
      const playerFromLineup = initialLineUp.find(
        (o) => o.uuid === selectedPlayer!.uuid,
      );
      /* eslint-enable @typescript-eslint/no-non-null-assertion */
      return playerObj.uuid === player.uuid ? playerFromLineup : playerObj;
    });

    const currentFormation = (
      playerFormationRef as HTMLElement
    ).querySelectorAll('.Player');
    /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    const oldPlayerIndex = currentLineUp.findIndex(
      (oldPlayer) => oldPlayer.uuid === selectedPlayer!.uuid,
    );
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    const oldPlayerEl = currentFormation[oldPlayerIndex] as HTMLElement;
    oldPlayerEl.style.transform = 'scale(1.2)';

    setTimeout(() => {
      oldPlayerEl.style.transform = 'scale(1)';
      dispatch({ type: 'MATCH_UPDATE_LINEUP', currentLineUp: newLineUp });
      dispatch({
        type: 'MATCH_UPDATE_SUBSTITUTES',
        substitutes: newSubstitutes as Player[],
      });
      setSelectedPlayer(null);
    }, 1000);
  };

  /**
   * Change player position
   *
   * @param player
   */
  const handleChangePosition = (player: Player) => {
    const newLineUp = currentLineUp.map((playerObj) => {
      /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
      return playerObj.uuid === selectedPlayer!.uuid
        ? player
        : playerObj && playerObj.uuid === player.uuid
          ? selectedPlayer
          : playerObj;
      /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
    });

    /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    const oldPlayerIndex = currentLineUp.findIndex(
      (oldPlayer) => oldPlayer.uuid === selectedPlayer!.uuid,
    );
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    const newPlayerIndex = currentLineUp.findIndex(
      (newPlayer) => newPlayer.uuid === player.uuid,
    );
    const currentFormation = (
      playerFormationRef as HTMLElement
    ).querySelectorAll('.Player');
    const oldPlayerEl = currentFormation[oldPlayerIndex] as HTMLElement;
    const newPlayerEl = currentFormation[newPlayerIndex] as HTMLElement;

    oldPlayerEl.style.transform = 'scale(1.2)';
    newPlayerEl.style.transform = 'scale(1.2)';

    setTimeout(() => {
      oldPlayerEl.style.transform = 'scale(1)';
      newPlayerEl.style.transform = 'scale(1)';

      dispatch({
        type: 'MATCH_UPDATE_LINEUP',
        currentLineUp: newLineUp as Player[],
      });
      setShowSidebar(false);
      setSidebarTabNav('substitute');
      setSelectedPlayer(null);
    }, 1000);
  };

  const handleSetLineUp = (player: Player) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    const updatedLineUp = [...newInitialLineUp!].map((lineUpPlayer, index) => {
      if (lineUpPlayer.uuid === player.uuid && index === selectedSpot) {
        return player;
      }

      // Check if player is already in field, replace if so
      if (lineUpPlayer.uuid === player.uuid && lineUpPlayer.uuid !== '') {
        return { name: '', uuid: '', icon: 'add' };
      }

      // Set player shirt to no icon, but keep selectable
      if (index === selectedSpot) {
        player.icon = 'change';
        return player;
      }

      return lineUpPlayer;
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setNewInitialLineUp!(updatedLineUp);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setSelectedModalPlayer!(null);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setSelectedSpot!(null);
  };

  const handleClick = (player: Player) => {
    if (editMode) return;
    if (clickablePlayers === 'modal') {
      handleSetLineUp(player);
      return;
    }
    if (!clickablePlayers) return;
    if (sidebarTabNav === 'substitute') handleChangeSubstitute(player);
    else handleChangePosition(player);
  };

  const updatePlayerPlayingTime = (
    e: ChangeEvent<HTMLInputElement>,
    player: Player,
  ) => {
    // Hard validation to check if value is not lower than 0 and not higher than matchduration
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    if ((e.currentTarget.value as unknown as number) > matchDuration!) {
      e.currentTarget.value = matchDuration as unknown as string;
    }
    if ((e.currentTarget.value as unknown as number) < 0) {
      e.currentTarget.value = 0 as unknown as string;
    }

    const newPlayingTime = parseInt(e.target.value);
    const newPlayer = { ...player };
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    const items = editLineUp!.length > 0 ? editLineUp! : players!;

    newPlayer.playingTime = newPlayingTime;
    const newLineUp = [...items].map((playerObj) => {
      if (playerObj.uuid === player.uuid) {
        return newPlayer;
      }
      return playerObj;
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setEditLineUp!(newLineUp);
  };

  return (
    <div className={`PlayerList${modifierClasses}`}>
      {playerList?.map((list, index) => {
        return (
          <ul className="PlayerList-list" key={`List${String(index)}`}>
            {list?.map((player, index) => {
              if (selectedPlayer && player.uuid === selectedPlayer.uuid) {
                return <Fragment key={player.uuid}></Fragment>;
              }
              /* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- chain resolves by contract here despite the optional type */
              return (
                <li
                  className="PlayerList-player"
                  onClick={() => {
                    handleClick(player);
                  }}
                  key={player.uuid}
                >
                  <span
                    className={`PlayerList-playerName ${player?.inField ? 'PlayerList-playerNameSet' : ''}`}
                  >
                    {/* TODO pass formation data */}
                    {showPositions && (
                      <span className="PlayerList-playerPosition">
                        {player?.position!}
                      </span>
                    )}
                    {player.name!}
                  </span>
                  {type === 'playingTime' && (
                    <>
                      {editMode ? (
                        <span className="PlayerList-playerTime has-padding">
                          <input
                            type="number"
                            defaultValue={player.playingTime as number}
                            min="0"
                            max={matchDuration}
                            name={`lineUpPlayer__${String(index)}`}
                            className="PlayerList-edit"
                            onChange={(e) => {
                              updatePlayerPlayingTime(e, player);
                            }}
                          />
                        </span>
                      ) : (
                        <span className="PlayerList-playerTime">
                          {/* Divide seconds to minutes in matchlog */}
                          {visible
                            ? Math.round((player.playingTime as number) / 60)
                            : (player.playingTime as number)}
                        </span>
                      )}
                    </>
                  )}

                  {player.inField! && (
                    <span className="PlayerList-inField">
                      {' '}
                      {t('MDL__TXT_DRAFTED')}{' '}
                    </span>
                  )}
                  {type === 'graph' && (
                    <PlayerGraph
                      totalPlayed={totalPlayed}
                      type="minutes"
                      player={player}
                      view={view}
                    />
                  )}
                </li>
              );
              /* eslint-enable @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
            })}
          </ul>
        );
      })}
    </div>
  );
};

PlayerList.defaultPropTypes = {
  modifiers: [],
  type: '',
  playerList: [],
  clickablePlayers: false,
  view: 'minutes',
  selectedSpot: 0,
  setSelectedSpot: () => {
    /* no-op */
  },
  setSelectedModalPlayer: () => {
    /* no-op */
  },
  newInitialLineUp: [],
  setNewInitialLineUp: () => {
    /* no-op */
  },
  totalPlayed: 0,
  showPositions: false,
  editMode: false,
  players: [],
  setEditLineUp: () => {
    /* no-op */
  },
};

export default PlayerList;
